import type { CogoKnowledgeRepository } from '@cogo/kg-repository'
import type { ChannelInput, ChannelOutput, DispatchTwoOpts } from '../_shared/channel-types'
import { corpusFewShotExamples } from './corpus-few-shot'
import { hashIntent, pickCorpusForChannelTwo } from './mock-corpus-picker'
import { mockStream } from './mock-stream'
import { intentToBlockSpec } from './intent-to-blockspec'
import { realStream } from './real-stream'
import { buildComposePreviewOutput } from './compose-preview'

const DEFAULT_TOTAL_MS = 30000
const DEFAULT_PAGE_COUNT = 5
const DEFAULT_CHUNK_MS = 1000

function shapeShaFor(input: ChannelInput): string {
  // Simple 8-char hex tag from FNV-1a hash.
  const h = hashIntent(input)
  return 'mock-' + h.toString(16).padStart(8, '0').slice(-8)
}

export async function* dispatchChannelTwo(
  input: ChannelInput,
  kg: CogoKnowledgeRepository,
  opts: DispatchTwoOpts = {},
): AsyncIterable<ChannelOutput> {
  if (opts.signal?.aborted) {
    yield { kind: 'miss', reason: 'aborted' }
    return
  }

  if (input.kind === 'examplePick') {
    yield {
      kind: 'miss',
      reason: 'unsupported-mode',
      details: 'examplePick mode is not supported by Channel 2',
    }
    return
  }

  // Real backend path — Phase 1 P1.2. Bypasses mock entirely.
  // `kg` is unused here (Channel 1 owns the KG path); we keep the signature
  // consistent so dispatchChannelTwo remains drop-in-replaceable in tests
  // that mock the kg.
  if (opts.realMode) {
    if (!opts.backendUrl) {
      yield {
        kind: 'miss',
        reason: 'backend-failed',
        details: 'realMode=true requires backendUrl',
      }
      return
    }
    const blockSpec = intentToBlockSpec(input)
    const examples = await corpusFewShotExamples(input, blockSpec)
    // `opts.realPipeline` is 'v2' by default (`ide-v2-slice.ts`), and the
    // route's 'v2' branch never builds a hybrid prompt — so examples sent
    // under 'v2' never reach an LLM. Escalate to the route's OWN default
    // ('hybrid-with-fallback', which retries with v2 when hybrid fails, so
    // no generation v2 could have produced is lost) when — and only when —
    // this request actually carries examples. An explicit 'hybrid'
    // (fail-fast, a deliberate developer choice on /labs/channel-2-real) is
    // left alone; an explicit 'v2' IS overridden, because it is
    // indistinguishable from the store default.
    const pipeline =
      examples.length > 0 && opts.realPipeline !== 'hybrid'
        ? ('hybrid-with-fallback' as const)
        : opts.realPipeline
    yield* realStream({
      blockSpec,
      backendUrl: opts.backendUrl,
      pipeline,
      examples,
      signal: opts.signal,
    })
    return
  }

  const mockMode = opts.mockMode ?? 'success'
  const shapeSha = opts.shapeSha ?? shapeShaFor(input)
  const totalDurationMs = opts.totalDurationMs ?? DEFAULT_TOTAL_MS
  const pageCount = opts.pageCount ?? DEFAULT_PAGE_COUNT
  const chunkIntervalMs = opts.chunkIntervalMs ?? DEFAULT_CHUNK_MS

  // mockMode=miss skips corpus entirely.
  if (mockMode === 'miss') {
    yield* mockStream({
      examples: [],
      shapeSha,
      totalDurationMs,
      pageCount,
      chunkIntervalMs,
      mockMode: 'miss',
      signal: opts.signal,
    })
    return
  }

  // Other modes need corpus.
  const pick = await pickCorpusForChannelTwo(input, kg, pageCount)
  if (pick.kind === 'insufficient') {
    yield {
      kind: 'miss',
      reason: 'no-candidates',
      details: `Corpus has ${pick.available} design-ready examples, need ${pick.required}; rejected ${pick.rejectedForQuality} below rendered design-quality floor`,
    }
    return
  }

  yield buildComposePreviewOutput(input, pick.examples)

  yield* mockStream({
    examples: pick.examples,
    shapeSha,
    totalDurationMs,
    pageCount,
    chunkIntervalMs,
    mockMode,
    signal: opts.signal,
  })
}
